import { useFrame } from '@react-three/fiber'
import { usePointCloudContext } from 'features/pointClouds'
import { usePointDetection } from 'features/pointClouds/hooks/usePointDetection'
import React, { useEffect, useRef, useState } from 'react'
import { Mesh, Vector3 } from 'three'

export function PointSelector({
  onHit,
  enabled,
  onClick,
}: {
  onHit?: (position: Vector3 | null) => void
  enabled: boolean
  onClick?: (position: Vector3, ev: MouseEvent) => void
}) {
  const hitMarkerRef = useRef<Mesh>(null)
  const cursorRef = useRef<Mesh>(null)
  const [isHit, setIsHit] = useState(false)
  const [currentHitPosition, setCurrentHitPosition] = useState<Vector3 | null>(
    null
  )
  const sizeRef = useRef({ current: 0.15, target: 0.15 })
  const { pointCloudOctreesRef } = usePointCloudContext()
  const { detectPoints } = usePointDetection(enabled)

  useEffect(() => {
    const handleClick = (ev: MouseEvent) => {
      if (enabled && isHit && currentHitPosition && onClick) {
        onClick(currentHitPosition, ev)
      }
    }

    const octrees = pointCloudOctreesRef.current

    window.addEventListener('click', handleClick)
    return () => {
      window.removeEventListener('click', handleClick)

      // Unhighlight all points
      octrees.forEach((pco) => {
        if (
          pco.material &&
          pco.material.uniforms &&
          pco.material.uniforms.segmentsToHighlight
        ) {
          pco.material.uniforms.segmentsToHighlight.value = [0, 0, 0, 0]
        }
      })
    }
  }, [enabled, isHit, currentHitPosition, onClick, pointCloudOctreesRef])

  useFrame((_, delta) => {
    if (!enabled) {
      if (hitMarkerRef.current) hitMarkerRef.current.visible = false
      if (cursorRef.current) cursorRef.current.visible = false
      if (onHit) onHit(null)
      setCurrentHitPosition(null)

      pointCloudOctreesRef.current.forEach((pco) => {
        if (
          pco.material &&
          pco.material.uniforms &&
          pco.material.uniforms.segmentsToHighlight
        ) {
          pco.material.uniforms.segmentsToHighlight.value = [0, 0, 0, 0]
        }
      })
      return
    }

    const { cursorPosition, bestHit } = detectPoints()

    if (cursorRef.current && cursorPosition) {
      cursorRef.current.visible = true
      cursorRef.current.position.copy(cursorPosition)
    }

    const isHit = bestHit !== null
    setIsHit(isHit)
    setCurrentHitPosition(bestHit?.point ?? null)

    if (hitMarkerRef.current) {
      hitMarkerRef.current.visible = isHit
      if (isHit) hitMarkerRef.current.position.copy(bestHit.point)
      sizeRef.current.target = isHit ? 0.4 : 0.15
    }

    if (onHit) onHit(bestHit?.point ?? null)

    pointCloudOctreesRef.current.forEach((pco) => {
      if (
        pco.material &&
        pco.material.uniforms &&
        pco.material.uniforms.segmentsToHighlight
      ) {
        pco.material.uniforms.segmentsToHighlight.value = [
          bestHit?.object.userData.segment ?? 0,
          0,
          0,
          0,
        ]
      }
    })

    sizeRef.current.current +=
      (sizeRef.current.target - sizeRef.current.current) * delta * 10
    if (hitMarkerRef.current && hitMarkerRef.current.scale) {
      const newSize = sizeRef.current.current
      hitMarkerRef.current.scale.set(newSize, newSize, newSize)
    }
  })

  return (
    <mesh ref={cursorRef} visible={false} renderOrder={999}>
      <sphereGeometry args={[0.05, 12, 12]} />
      <meshBasicMaterial
        color={isHit ? 0xff0000 : 0xffffff}
        transparent={true}
        opacity={0.6}
        wireframe={true}
        depthWrite={false}
        depthTest={false}
      />
    </mesh>
  )
}
